import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { AlertTriangle, CreditCard, X } from "lucide-react";
import CardUpdateModal from "./CardUpdateModal";

/**
 * Sticky in-app banner that nudges users with broken billing to add a
 * card. Two trigger states:
 *
 *  • PAST_DUE          — Square `invoice.payment_failed` fired. They have
 *                        ~7 days before auto-downgrade.
 *  • MISSING_SUB       — They're on a paid tier but have no Square
 *                        subscription (e.g. Shean — paid once via the
 *                        legacy Payment Link, no recurring sub created).
 *                        No auto-downgrade triggers for these users, so
 *                        the banner is their main nudge to fix it.
 *
 * Clicking "Update card" opens an in-app `<CardUpdateModal />` (Square
 * Web Payments SDK) — no redirect, no separate page. On success the
 * backend auto-creates the recurring Subscription (start_date = today +
 * 30d) and we refresh the AuthContext user so the banner disappears.
 *
 * Dismissible per-session (sessionStorage).
 */
// All currently-billable tiers — anyone on one of these tiers needs a
// card on file. Includes deprecated paid tiers that are closed to new
// signups but where existing customers still pay:
//   • advanced    £4.99/mo  (deprecated)
//   • elite       £19.99/mo (deprecated)
//   • pro-legacy  £9.99/mo  (grandfathered old Pro price — NOT comp)
// Source of truth: TIER_PRICES in /app/backend/models.py.
const PAID_TIERS = ["solo", "pro", "crew", "advanced", "elite", "pro-legacy"];

// Tiers that are GENUINELY £0 / comp by definition. Keep this list in
// sync with the £0-priced entries in TIER_LIMITS (models.py).
const LEGACY_TIERS = ["legacy", "unlimited_beta"];

// Boolean comp flags — admin / cert beta tester / ambassador etc. All
// imply "do not bill". `founder_plan` is intentionally EXCLUDED — it's
// just a price-lock stamp, not a free pass.
const COMP_FLAG_KEYS = [
  "legacy_free_pro",
  "is_beta_tester",
  "is_certificate_beta_tester",
  "is_ambassador",
  "is_reseller",
  "lifetime_access",
];

// Roles that bypass billing entirely (set on user.role, not a flag).
const COMP_ROLES = ["admin"];

const SESSION_DISMISS_KEY = "qs_pastdue_banner_dismissed";

export default function PaymentFailedBanner() {
  const { user, refreshUser } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SESSION_DISMISS_KEY) === "1"
  );

  if (!user || dismissed) return null;

  const tier = (user.tier || "").toLowerCase();
  const role = (user.role || "").toLowerCase();
  const isPaidTier = PAID_TIERS.includes(tier);
  const isLegacyTier = LEGACY_TIERS.includes(tier);
  const status = (user.subscription_status || "").toUpperCase();
  const hasSub = !!user.has_square_subscription;
  const isCompRole = COMP_ROLES.includes(role);
  const isCompFlag = COMP_FLAG_KEYS.some((k) => user[k]);
  // Legacy tiers are comp by definition.
  const isComp = isCompRole || isCompFlag || isLegacyTier;
  const inTrial = !!user.trial_tier && !user.trial_expired;

  // Comp accounts (admins, founders, beta testers, legacy tiers, cert
  // beta testers etc) bypass every billing banner — they don't pay, so
  // they should never be nagged regardless of what `subscription_status`
  // happens to show.
  if (isComp) return null;

  const isPastDue = status === "PAST_DUE";
  const isMissingSub = isPaidTier && !hasSub && !inTrial && status !== "CANCELED";

  if (!isPastDue && !isMissingSub) return null;

  const dismiss = () => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    setDismissed(true);
  };

  const handleSuccess = async () => {
    // Re-fetch the user record so `has_square_subscription` flips and
    // the banner naturally disappears via the gate above.
    await refreshUser?.();
    setModalOpen(false);
  };

  const isUrgent = isPastDue;
  const wrapperCls = isUrgent
    ? "bg-rose-600 text-white"
    : "bg-amber-500 text-amber-950";
  const iconCls = isUrgent ? "text-rose-100" : "text-amber-900";

  const headline = isPastDue
    ? "Your last payment was declined"
    : "Add a card to keep your subscription active";

  const body = isPastDue
    ? "Update your card in the next 7 days to avoid losing access. Square will retry your card a few more times automatically."
    : `You're on the ${tier.toUpperCase()} plan but we don't have a card on file for recurring billing. Add one to continue uninterrupted.`;

  const ctaLabel = isPastDue ? "Update card" : "Add card";
  const tierLabel = (user.tier || "your").charAt(0).toUpperCase() + (user.tier || "").slice(1);

  return (
    <>
      <div
        className={`sticky top-0 z-40 w-full shadow-md ${wrapperCls}`}
        data-testid="payment-failed-banner"
        role="alert"
      >
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3">
          <AlertTriangle className={`w-5 h-5 shrink-0 ${iconCls}`} />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm sm:text-base leading-tight" data-testid="banner-headline">
              {headline}
            </p>
            <p className="text-xs sm:text-[13px] opacity-90 mt-0.5 leading-snug hidden sm:block">
              {body}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-bold shadow-sm shrink-0 transition-colors ${
              isUrgent
                ? "bg-white text-rose-700 hover:bg-rose-50"
                : "bg-amber-950 text-amber-50 hover:bg-amber-900"
            }`}
            data-testid="banner-update-card-btn"
          >
            <CreditCard className="w-3.5 h-3.5" /> {ctaLabel}
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss banner"
            className={`p-1.5 rounded-md shrink-0 transition-colors ${
              isUrgent ? "hover:bg-rose-700" : "hover:bg-amber-600"
            }`}
            data-testid="banner-dismiss-btn"
            title="Dismiss until next login"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <CardUpdateModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onSuccess={handleSuccess}
        mode={isPastDue ? "past_due" : "missing_sub"}
        tierLabel={tierLabel}
      />
    </>
  );
}
